import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleInfo,
  faCompress,
  faExpand,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import CustomInput from "../components/CustomInput";
import Loader from "../components/Loader";
import axiosInstance from "../utils/axiosInstance";
import { ParseErrors } from "../utils/ParseErrors";
import { useEditV2PlanModel } from "../store/use-edit-v2-plan-model";
import V2PlanTimeline, {
  TScheduleItem,
} from "../components/chaseListComponents/v2-plan-timeline";

/** Design-only for now: the CDL amend calls aren't connected, so saving stays disabled */
export default function EditV2PlanModel() {
  const { initialValues, onClose } = useEditV2PlanModel();
  const chase = initialValues?.data;
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    nextPaymentDate: "",
    instalmentsLeft: "",
    amountOwing: "",
  });
  const [schedule, setSchedule] = useState<TScheduleItem[]>([]);
  const [expanded, setExpanded] = useState(false);

  // the popup only mounts when it's open, so this runs once per open
  useEffect(() => {
    const getPlan = async () => {
      try {
        const res = await axiosInstance.get(
          `/loan/v2-plan/${initialValues?.id}`,
        );
        setFormData({
          nextPaymentDate: res.data?.nextPaymentDate ?? "",
          instalmentsLeft: res.data?.instalmentsLeft ?? "",
          amountOwing: res.data?.amountOwing ?? "",
        });
        setSchedule(res.data?.schedule ?? []);
      } catch (error) {
        toast.error(ParseErrors(error));
      } finally {
        setLoading(false);
      }
    };

    getPlan();
  }, [initialValues?.id]);

  const amountPerInstalment =
    Number(formData.amountOwing) > 0 && Number(formData.instalmentsLeft) > 0
      ? (
          Number(formData.amountOwing) / Number(formData.instalmentsLeft)
        ).toFixed(2)
      : null;

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const HandleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="edit-v2-plan-title"
      className={clsx(
        "z-[99999] fixed inset-0 bg-[color:var(--shadow-modal-scrim)] backdrop-blur-sm flex items-center justify-center animate-scrim-in",
        !expanded && "p-4",
      )}
      onClick={onClose}
    >
      <div
        className={clsx(
          "w-full overflow-y-auto bg-surface border border-edge shadow-overlay p-7 max-sm:p-5 animate-modal-in",
          expanded
            ? "h-screen max-w-none rounded-none"
            : "max-w-[880px] max-h-[92vh] rounded-2xl",
        )}
        onClick={(e) => e.stopPropagation()}
        data-testid="edit-v2-plan-panel"
      >
        <div className="flex items-start justify-between gap-3">
          <h2
            id="edit-v2-plan-title"
            className="text-[22px] font-bold text-text-primary leading-snug"
          >
            Edit plan (V2)
          </h2>
          <button
            type="button"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? "Collapse" : "Expand"}
            title={expanded ? "Collapse" : "Expand"}
            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-xl text-text-secondary bg-surface border border-edge hover:border-edge-strong hover:bg-overlay-hover transition-all duration-200 cursor-pointer"
          >
            <FontAwesomeIcon
              icon={expanded ? faCompress : faExpand}
              className="text-[14px]"
            />
          </button>
        </div>
        <p className="mt-1 text-[13px] font-medium text-light-gray">
          {chase?.policyReferenceNumber}
          {chase?.firstName
            ? ` – ${chase?.firstName} ${chase?.surname ?? ""}`
            : ""}
        </p>

        {!loading ? <V2PlanTimeline schedule={schedule} /> : null}

        <div className="mt-5 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-surface-elevated border border-edge px-4 py-3">
            <p className="text-[12px] font-medium text-light-gray">
              Outstanding balance
            </p>
            <p className="mt-1 text-[18px] font-bold text-heading-color tabular-nums">
              £{Number(chase?.outstandingBalance ?? 0).toFixed(2)}
            </p>
          </div>
          <div className="rounded-xl bg-surface-elevated border border-edge px-4 py-3">
            <p className="text-[12px] font-medium text-light-gray">
              Amount per instalment
            </p>
            <p
              className="mt-1 text-[18px] font-bold text-heading-color tabular-nums"
              data-testid="amount-per-instalment"
            >
              {amountPerInstalment ? `£${amountPerInstalment}` : "–"}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="mt-5 h-[148px]" data-testid="v2-plan-loading">
            <Loader />
          </div>
        ) : (
          <div className="mt-5 grid grid-cols-3 max-sm:grid-cols-1 gap-4">
            <div>
              <CustomInput
                id="nextPaymentDate"
                name="nextPaymentDate"
                type="date"
                label="Next payment date"
                min={moment().add(1, "day").format("YYYY-MM-DD")}
                value={formData.nextPaymentDate}
                onChange={HandleChange}
                disabled
              />
            </div>
            <CustomInput
              id="instalmentsLeft"
              name="instalmentsLeft"
              type="number"
              label="Instalments left"
              min={1}
              max={12}
              placeholder="e.g. 3"
              value={formData.instalmentsLeft}
              onChange={HandleChange}
              disabled
            />
            <CustomInput
              id="amountOwing"
              name="amountOwing"
              type="number"
              label="Amount owing"
              startIcon="£"
              allowDecimal
              min={0}
              value={formData.amountOwing}
              onChange={HandleChange}
              disabled
            />
          </div>
        )}

        <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-edge bg-overlay px-4 py-3">
          <FontAwesomeIcon
            icon={faCircleInfo}
            className="text-accent text-[13px] mt-0.5"
          />
          <p className="text-[13px] font-medium text-text-secondary leading-relaxed">
            Saving isn't available yet. Changes here won't be sent to the
            customer's plan.
          </p>
        </div>

        <div className="flex items-center justify-end gap-3 mt-7">
          <button
            type="button"
            onClick={onClose}
            className="h-11 px-6 rounded-xl text-[15px] font-semibold text-text-primary bg-surface border border-edge hover:border-edge-strong hover:bg-overlay-hover transition-all duration-200 cursor-pointer"
          >
            Close
          </button>
          <button
            type="button"
            disabled
            title="Not available yet"
            className="h-11 px-6 rounded-xl text-[15px] font-semibold text-white border border-white/10 bg-gradient-to-r from-accent to-accent-bright min-w-[120px] opacity-60 cursor-not-allowed"
          >
            Save changes
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
